/**
 * Sentry browser bootstrap for the Telegram Mini App (TMA).
 *
 * Mirrors the env-gated `services/api` Sentry init: the DSN is read from
 * the environment and, when it is unset, `init` is a clean no-op so dev
 * boxes (and the founder's first CI builds, before the Sentry projects
 * exist) run normally without credentials. Environment + release are
 * tagged the same way as the API.
 *
 * Called from `main.tsx` **before** `createRoot(...)` so the SDK captures
 * errors thrown during the initial mount. The DSN comes from Vite's
 * `import.meta.env.VITE_*` namespace — TMA builds inline these at compile
 * time, which keeps prod / staging DSNs separate.
 *
 * Adds the Telegram user (id only, no name / username / phone) to Sentry's
 * scope as soon as the WebApp SDK exposes `initDataUnsafe`. That lets us
 * correlate alerts with users without violating PII scrubbing — the id is
 * a stable surrogate (`sendDefaultPii: false`).
 */
import * as Sentry from "@sentry/react";

interface TelegramWebAppUser {
  id?: number;
}

interface TelegramWebAppInitDataUnsafe {
  user?: TelegramWebAppUser;
}

interface TelegramWebApp {
  initDataUnsafe?: TelegramWebAppInitDataUnsafe;
}

interface TelegramGlobal {
  WebApp?: TelegramWebApp;
}

const dsn = import.meta.env.VITE_SENTRY_DSN_TMA;
const environment = import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE;
const release = import.meta.env.VITE_APP_VERSION;

let initialised = false;

/**
 * Initialise the Sentry browser SDK. No-op when `VITE_SENTRY_DSN_TMA`
 * is empty (the app boots normally without it) and idempotent across
 * repeated calls (React StrictMode double-invokes effects).
 */
export function initSentry(): void {
  if (initialised) return;
  initialised = true;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    // Matches the API: lower trace sampling in prod, full in dev.
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,
    integrations: [Sentry.browserTracingIntegration()],
  });

  attachTelegramUser();
}

function attachTelegramUser(): void {
  if (typeof window === "undefined") return;
  const telegram = (window as unknown as { Telegram?: TelegramGlobal }).Telegram;
  const userId = telegram?.WebApp?.initDataUnsafe?.user?.id;
  if (typeof userId === "number") {
    Sentry.setUser({ id: String(userId) });
  }
}

/**
 * React 19 error hook factory re-exported for `createRoot`'s
 * `onUncaughtError` / `onCaughtError` / `onRecoverableError` options.
 * Safe to wire unconditionally: when Sentry was not initialised (no DSN)
 * the handler simply has no client to report to.
 */
export const reactErrorHandler = Sentry.reactErrorHandler;

/** Root-level error boundary component (`<SentryErrorBoundary>`). */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
